import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from './AuthContext';
import { postData } from './apiService';
import { jwtDecode } from 'jwt-decode';
import { toast } from 'react-toastify';

const SessionTimeout = () => {
    const { login, ClearToken, token, logout, companyName, isAuthenticated } = useAuth();
    const [showWarning, setShowWarning] = useState(false);
    const [countdown, setCountdown] = useState(60);
    const [warningLevel, setWarningLevel] = useState(0); // 0: none, 1: early, 2: urgent, 3: critical
    const [lastActivityTime, setLastActivityTime] = useState(Date.now());
    
    // Inactivity-based session configuration
    const inactivityTimeout = 3600000; // 1 hour of inactivity (3600000ms)
    const earlyWarningTime = 600000; // 10 minutes before timeout (show early warning)
    const warningTime = 300000; // 5 minutes before timeout (show modal)
    const urgentWarningTime = 120000; // 2 minutes before timeout (urgent warning)
    const warningCountdown = 60; // 60 seconds countdown in warning modal
    
    // Use refs to store timer IDs and activity tracking
    const inactivityTimerRef = useRef(null);
    const earlyWarningTimerRef = useRef(null);
    const warningTimerRef = useRef(null);
    const urgentWarningTimerRef = useRef(null);
    const logoutTimerRef = useRef(null);
    const countdownTimerRef = useRef(null);
    const sessionCheckIntervalRef = useRef(null);
    const lastWarningShownRef = useRef(0);
    const lastActivityTimeRef = useRef(Date.now());

    const resetTimers = useCallback(() => {
        if (inactivityTimerRef.current) {
            clearTimeout(inactivityTimerRef.current);
            inactivityTimerRef.current = null;
        }
        if (earlyWarningTimerRef.current) {
            clearTimeout(earlyWarningTimerRef.current);
            earlyWarningTimerRef.current = null;
        }
        if (warningTimerRef.current) {
            clearTimeout(warningTimerRef.current);
            warningTimerRef.current = null;
        }
        if (urgentWarningTimerRef.current) {
            clearTimeout(urgentWarningTimerRef.current);
            urgentWarningTimerRef.current = null;
        }
        if (logoutTimerRef.current) {
            clearTimeout(logoutTimerRef.current);
            logoutTimerRef.current = null;
        }
        if (countdownTimerRef.current) {
            clearInterval(countdownTimerRef.current);
            countdownTimerRef.current = null;
        }
        setWarningLevel(0);
    }, []);

    // Get time since last activity
    const getInactivityTime = useCallback(() => {
        return Date.now() - lastActivityTimeRef.current;
    }, []);

    // Get remaining time before inactivity timeout
    const getRemainingInactivityTime = useCallback(() => {
        const inactiveTime = getInactivityTime();
        return Math.max(0, inactivityTimeout - inactiveTime);
    }, [getInactivityTime, inactivityTimeout]);

    // Show different types of warnings based on inactivity time
    const showEarlyWarning = useCallback(() => {
        if (lastWarningShownRef.current < 1) {
            const inactiveMinutes = Math.floor(getInactivityTime() / 60000);
            toast.warning(`⏰ You've been inactive for ${inactiveMinutes} minutes. Session will timeout in 10 minutes if no activity detected.`, {
                position: 'top-right',
                autoClose: 8000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
            });
            setWarningLevel(1);
            lastWarningShownRef.current = 1;
        }
    }, [getInactivityTime]);

    const showUrgentWarning = useCallback(() => {
        if (lastWarningShownRef.current < 2) {
            toast.error('🚨 You will be logged out in 2 minutes due to inactivity! Move your mouse or click anywhere to stay logged in.', {
                position: 'top-center',
                autoClose: 10000,
                hideProgressBar: false,
                closeOnClick: false,
                pauseOnHover: true,
                draggable: false,
            });
            setWarningLevel(2);
            lastWarningShownRef.current = 2;
        }
    }, []);

    const showCriticalWarning = useCallback(() => {
        setShowWarning(true);
        setWarningLevel(3);
        startCountdown();
        
        // Set final logout timer
        logoutTimerRef.current = setTimeout(() => {
            setShowWarning(false);
            toast.error('Session expired due to inactivity.');
            logout();
        }, warningCountdown * 1000);
    }, [logout, warningCountdown]);
    // Check if current session is expired
    const checkSessionExpiry = useCallback(() => {
        if (!token || !isAuthenticated) return false;
        
        try {
            const decodedToken = jwtDecode(token);
            const currentTimestamp = Date.now() / 1000;
            
            if (decodedToken.exp < currentTimestamp) {
                console.log('Session expired, logging out...');
                toast.error('Your session has expired. Please login again.');
                logout();
                return true;
            }
            return false;
        } catch (error) {
            console.error('Error checking session expiry:', error);
            logout();
            return true;
        }
    }, [token, isAuthenticated, logout]);

    // Get remaining session time in seconds
    const getRemainingTime = useCallback(() => {
        if (!token) return 0;
        
        try {
            const decodedToken = jwtDecode(token);
            const currentTimestamp = Date.now() / 1000;
            return Math.max(0, decodedToken.exp - currentTimestamp);
        } catch (error) {
            return 0;
        }
    }, [token]);

    // Start countdown in warning modal
    const startCountdown = useCallback(() => {
        setCountdown(warningCountdown);
        
        countdownTimerRef.current = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    setShowWarning(false);
                    logout();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, [logout, warningCountdown]);

    // Handle user activity and reset inactivity timers
    const handleActivity = useCallback(() => {
        // Don't set timers if user is not authenticated
        if (!isAuthenticated || !token) return;
        
        // Check if session token is still valid
        if (checkSessionExpiry()) return;
        
        // Update last activity time
        const currentTime = Date.now();
        lastActivityTimeRef.current = currentTime;
        setLastActivityTime(currentTime);
        
        // Store in localStorage for synchronization with status indicator
        localStorage.setItem('lastActivityTime', currentTime.toString());
        
        // Reset all timers and warnings
        resetTimers();
        setShowWarning(false);
        lastWarningShownRef.current = 0;
        
        console.log('User activity detected, resetting inactivity timers...');
        
        // Set early warning timer (50 minutes of inactivity)
        earlyWarningTimerRef.current = setTimeout(() => {
            if (!checkSessionExpiry()) {
                showEarlyWarning();
            }
        }, inactivityTimeout - earlyWarningTime);
        
        // Set urgent warning timer (58 minutes of inactivity)
        urgentWarningTimerRef.current = setTimeout(() => {
            if (!checkSessionExpiry()) {
                showUrgentWarning();
            }
        }, inactivityTimeout - urgentWarningTime);
        
        // Set critical warning timer (55 minutes of inactivity - show modal)
        warningTimerRef.current = setTimeout(() => {
            if (!checkSessionExpiry()) {
                showCriticalWarning();
            }
        }, inactivityTimeout - warningTime);
        
    }, [isAuthenticated, token, checkSessionExpiry, resetTimers, inactivityTimeout, earlyWarningTime, urgentWarningTime, warningTime, showEarlyWarning, showUrgentWarning, showCriticalWarning]);

    // Periodic inactivity check with progressive warnings and localStorage sync
    useEffect(() => {
        if (isAuthenticated && token) {
            sessionCheckIntervalRef.current = setInterval(() => {
                if (checkSessionExpiry()) return;
                
                const inactiveTime = getInactivityTime();
                const inactiveMinutes = Math.floor(inactiveTime / 60000);
                const remainingTime = getRemainingInactivityTime();
                const remainingMinutes = Math.floor(remainingTime / 60000);
                
                // Update localStorage for status indicator synchronization
                localStorage.setItem('lastActivityTime', lastActivityTimeRef.current.toString());
                
                console.log(`Inactive for ${inactiveMinutes} minutes, ${remainingMinutes} minutes until timeout`);
                
                // Show progressive warnings based on inactivity time
                if (inactiveTime >= (inactivityTimeout - earlyWarningTime) && lastWarningShownRef.current < 1) {
                    showEarlyWarning();
                } else if (inactiveTime >= (inactivityTimeout - warningTime) && lastWarningShownRef.current < 1.5) {
                    toast.warning(`⚠️ You've been inactive for ${inactiveMinutes} minutes! Activity required in ${remainingMinutes} minutes.`, {
                        position: 'top-center',
                        autoClose: 6000,
                    });
                    lastWarningShownRef.current = 1.5;
                } else if (inactiveTime >= (inactivityTimeout - urgentWarningTime) && lastWarningShownRef.current < 2) {
                    showUrgentWarning();
                } else if (inactiveTime >= (inactivityTimeout - 60000) && !showWarning) { // 1 minute before timeout
                    showCriticalWarning();
                }
            }, 5000); // Check every 5 seconds for better responsiveness
        }
        
        return () => {
            if (sessionCheckIntervalRef.current) {
                clearInterval(sessionCheckIntervalRef.current);
                sessionCheckIntervalRef.current = null;
            }
        };
    }, [isAuthenticated, token, checkSessionExpiry, getInactivityTime, getRemainingInactivityTime, inactivityTimeout, earlyWarningTime, warningTime, urgentWarningTime, showEarlyWarning, showUrgentWarning, showCriticalWarning, showWarning]);

    // Set up activity listeners and initial timer
    useEffect(() => {
        if (!isAuthenticated || !token) {
            resetTimers();
            setShowWarning(false);
            return;
        }

        // Check session on component mount/token change
        if (checkSessionExpiry()) return;
        
        // Initialize activity time
        lastActivityTimeRef.current = Date.now();
        setLastActivityTime(Date.now());
        
        // Start activity monitoring
        handleActivity();

        // Activity event listeners - more comprehensive list
        const events = ['mousemove', 'keydown', 'click', 'scroll', 'touchstart', 'mousedown', 'keypress', 'focus', 'blur'];
        
        // Throttle activity handler to prevent excessive calls but ensure responsiveness
        let activityThrottle = null;
        const throttledActivityHandler = () => {
            if (activityThrottle) return;
            
            // Update localStorage immediately for real-time sync
            const currentTime = Date.now();
            localStorage.setItem('lastActivityTime', currentTime.toString());
            
            activityThrottle = setTimeout(() => {
                handleActivity();
                activityThrottle = null;
            }, 2000); // Reduced throttle to 2 seconds for better responsiveness
        };
        
        events.forEach(event => {
            window.addEventListener(event, throttledActivityHandler, { passive: true });
        });

        return () => {
            resetTimers();
            if (activityThrottle) {
                clearTimeout(activityThrottle);
            }
            events.forEach(event => {
                window.removeEventListener(event, throttledActivityHandler);
            });
        };
    }, [isAuthenticated, token, handleActivity, checkSessionExpiry, resetTimers]);

    // Cleanup on unmount
    useEffect(() => {
        return () => {
            resetTimers();
            if (sessionCheckIntervalRef.current) {
                clearInterval(sessionCheckIntervalRef.current);
            }
        };
    }, [resetTimers]);

    // Handle manual logout
    const handleLogout = useCallback(() => {
        resetTimers();
        setShowWarning(false);
        logout();
    }, [resetTimers, logout]);

    // Refresh session token (manual refresh from modal or activity-based extension)
    const handleRefresh = useCallback(async () => {
        try {
            setShowWarning(false);
            resetTimers();
            lastWarningShownRef.current = 0; // Reset warning level
            
            const jwtToken = await performLogin();
            const responseData = jwtDecode(jwtToken);
            const keysToRemove = ['iss', 'exp', 'ttp', 'http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name', 'aud'];
            const permissions = Object.keys(responseData).filter(
                (key) => key !== 'companyId' && key !== 'userId' && !keysToRemove.includes(key)
            );

            let isSuper = false;
            if (responseData.isSuperAdmin && responseData.isSuperAdmin.toLowerCase() === "true") {
                isSuper = true;
            }

            // Convert isAppointmentNeeded from "Yes"/"No" to boolean
            let isAppointmentNeeded = false;
            if (responseData.isAppointmentNeeded && responseData.isAppointmentNeeded.toLowerCase() === "yes") {
                isAppointmentNeeded = true;
            }

            let compname = companyName;
            ClearToken();

            login(
                responseData.companyId, 
                responseData.branchId, 
                responseData.userId, 
                isSuper, 
                responseData.superAdminCompanyId, 
                compname, 
                permissions, 
                jwtToken, 
                responseData.roleName, 
                responseData.branchstate, 
                responseData.username, 
                isAppointmentNeeded
            );
            
            toast.success('✅ Session extended successfully! Inactivity timer reset.', {
                position: 'top-center',
                autoClose: 5000,
            });
            
            // Reset activity time and restart monitoring
            const currentTime = Date.now();
            lastActivityTimeRef.current = currentTime;
            setLastActivityTime(currentTime);
            localStorage.setItem('lastActivityTime', currentTime.toString());
            
            // Restart timers manually to avoid circular dependency
            resetTimers();
            setShowWarning(false);
            lastWarningShownRef.current = 0;
            
            // Set early warning timer (50 minutes of inactivity)
            earlyWarningTimerRef.current = setTimeout(() => {
                if (!checkSessionExpiry()) {
                    showEarlyWarning();
                }
            }, inactivityTimeout - earlyWarningTime);
            
            // Set urgent warning timer (58 minutes of inactivity)
            urgentWarningTimerRef.current = setTimeout(() => {
                if (!checkSessionExpiry()) {
                    showUrgentWarning();
                }
            }, inactivityTimeout - urgentWarningTime);
            
            // Set critical warning timer (55 minutes of inactivity - show modal)
            warningTimerRef.current = setTimeout(() => {
                if (!checkSessionExpiry()) {
                    showCriticalWarning();
                }
            }, inactivityTimeout - warningTime);
        } catch (error) {
            console.error('Session refresh failed:', error);
            toast.error('Failed to refresh session. Please login again.');
            handleLogout();
        }
    }, [resetTimers, companyName, ClearToken, login, handleLogout, checkSessionExpiry, showEarlyWarning, showUrgentWarning, showCriticalWarning, inactivityTimeout, earlyWarningTime, urgentWarningTime, warningTime]);

    // Perform token refresh API call
    const performLogin = async () => {
        const decodedToken = jwtDecode(token);

        const currentTimestamp = Date.now() / 1000;
        if (decodedToken.exp < currentTimestamp) {
            throw new Error('Token has already expired');
        }

        const loginData = {
            companyId: decodedToken.companyId,
            branchId: decodedToken.branchId,
            userId: decodedToken.userId,
            username: decodedToken.username
        };

        const response = await postData('UserTokenApi', loginData, token);

        if (response.responseCode === '1111') {
            return response.response_data.token;
        } else {
            throw new Error(response.Message || 'Token refresh failed');
        }
    };

    // Don't render anything if user is not authenticated
    if (!isAuthenticated) {
        return null;
    }

    const styles = {
        warningContainer: {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            backgroundColor: 'rgba(0, 0, 0, 0.8)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 9999,
            backdropFilter: 'blur(8px)',
            animation: 'fadeIn 0.3s ease-out',
        },
        warningBox: {
            backgroundColor: 'white',
            padding: '35px',
            borderRadius: '16px',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.4)',
            textAlign: 'center',
            maxWidth: '450px',
            width: '90%',
            border: warningLevel === 3 ? '4px solid #ef4444' : '3px solid #f59e0b',
            animation: 'slideUp 0.4s ease-out',
        },
        title: {
            color: warningLevel === 3 ? '#ef4444' : '#f59e0b',
            fontSize: '22px',
            fontWeight: 'bold',
            marginBottom: '18px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
        },
        message: {
            color: '#374151',
            fontSize: '16px',
            marginBottom: '25px',
            lineHeight: '1.6',
        },
        countdown: {
            color: warningLevel === 3 ? '#ef4444' : '#f59e0b',
            fontSize: '32px',
            fontWeight: 'bold',
            marginBottom: '25px',
            padding: '15px',
            borderRadius: '12px',
            background: warningLevel === 3 ? '#fef2f2' : '#fef3c7',
            border: `2px solid ${warningLevel === 3 ? '#fecaca' : '#fde68a'}`,
        },
        buttonContainer: {
            display: 'flex',
            gap: '15px',
            justifyContent: 'center',
            flexWrap: 'wrap',
        },
        button: {
            padding: '14px 28px',
            fontSize: '15px',
            cursor: 'pointer',
            borderRadius: '8px',
            border: 'none',
            fontWeight: 'bold',
            transition: 'all 0.3s ease',
            minWidth: '120px',
        },
        refreshButton: {
            background: 'linear-gradient(135deg, #10b981, #059669)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.3)',
        },
        logoutButton: {
            background: 'linear-gradient(135deg, #ef4444, #dc2626)',
            color: 'white',
            boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
        },
        warningIcon: {
            fontSize: '28px',
            animation: warningLevel === 3 ? 'pulse 1s infinite' : 'none',
        },
        pulseAnimation: `
            @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.5; }
            }
            @keyframes fadeIn {
                from { opacity: 0; }
                to { opacity: 1; }
            }
            @keyframes slideUp {
                from { opacity: 0; transform: translateY(30px) scale(0.9); }
                to { opacity: 1; transform: translateY(0) scale(1); }
            }
        `
    };

    return (
        <>
            <style>{styles.pulseAnimation}</style>
            {showWarning && (
                <div style={styles.warningContainer}>
                    <div style={styles.warningBox}>
                        <div style={styles.title}>
                            <span style={styles.warningIcon}>
                                {warningLevel === 3 ? '🚨' : '⚠️'}
                            </span>
                            {warningLevel === 3 ? 'INACTIVITY TIMEOUT!' : 'Inactivity Warning'}
                        </div>
                        <div style={styles.message}>
                            {warningLevel === 3 ? (
                                <>
                                    <strong>You will be logged out in {countdown} seconds due to inactivity!</strong>
                                    <br />
                                    <br />
                                    You have been inactive for over 1 hour. Click "Stay Logged In" to continue working,
                                    or you will be automatically logged out for security.
                                    <br />
                                    <br />
                                    <em>Any mouse movement, keyboard input, or clicking will reset the inactivity timer.</em>
                                </>
                            ) : (
                                <>
                                    You have been inactive for an extended period.
                                    <br />
                                    <br />
                                    Would you like to stay logged in and reset the inactivity timer?
                                </>
                            )}
                        </div>
                        <div style={styles.countdown}>
                            {countdown}s
                        </div>
                        <div style={styles.buttonContainer}>
                            <button 
                                style={{ ...styles.button, ...styles.refreshButton }} 
                                onClick={handleRefresh}
                            >
                                🔄 Stay Logged In
                            </button>
                            <button 
                                style={{ ...styles.button, ...styles.logoutButton }} 
                                onClick={handleLogout}
                            >
                                🚪 Logout Now
                            </button>
                        </div>
                        {warningLevel === 3 && (
                            <div style={{ 
                                marginTop: '20px', 
                                fontSize: '12px', 
                                color: '#6b7280',
                                fontStyle: 'italic' 
                            }}>
                                💡 Tip: Any activity (mouse movement, keyboard input, clicking) will reset the inactivity timer
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
};

export default SessionTimeout;
